import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Lock } from 'lucide-react';
import { formatMoney } from '../../lib/format.js';

const EASE = [0.2, 0.7, 0.2, 1];

/**
 * The checkout, as a sequence of small screens rather than one long form.
 *
 * Everything used to be stacked in one column — contact, method list, card
 * preview, card fields, test cards, pay button — which scrolled inside the
 * drop-in modal. A customer had to scroll to find out what the next thing to do
 * was, and a checkout that has to be explored is a checkout people abandon.
 *
 * One decision per screen. The amount and the merchant stay pinned at the top
 * throughout, because the two questions a customer asks at every step are "who
 * am I paying" and "how much".
 */
export function StepFrame({ merchant, payment, step, steps, onBack, title, blurb, children }) {
  const accent = /^#[0-9a-fA-F]{6}$/.test(merchant?.accentColor ?? '') ? merchant.accentColor : '#386db2';
  const index = steps.indexOf(step);

  return (
    <div className="flex min-h-full flex-col">
      {/* ------------------------------ pinned head ----------------------------- */}
      <div
        className="sticky top-0 z-10 border-b bg-surface/95 px-5 py-3.5 backdrop-blur sm:px-6"
        style={{ borderColor: 'rgb(var(--line))' }}
      >
        <div className="flex items-center gap-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="-ml-1.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-surface-2"
              aria-label="Back"
            >
              <ChevronLeft className="h-4.5 w-4.5" />
            </button>
          ) : (
            <span
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[13px] font-semibold text-white"
              style={{ background: accent }}
              aria-hidden="true"
            >
              {(merchant?.displayName ?? 'S').charAt(0).toUpperCase()}
            </span>
          )}

          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium leading-tight text-ink">
              {merchant?.displayName}
            </p>
            {payment?.orderId && (
              <p className="truncate text-[11px] leading-tight text-muted">{payment.orderId}</p>
            )}
          </div>

          <p className="tabular shrink-0 text-[17px] font-semibold tracking-tight text-ink">
            {formatMoney(payment?.amount, payment?.currency)}
          </p>
        </div>

        {/* A progress rule, not a numbered stepper — the count changes with the
            methods a merchant has enabled, and "step 2 of 3" that sometimes says
            "of 2" is worse than no number at all. */}
        {steps.length > 1 && (
          <div className="mt-3 flex gap-1" aria-hidden="true">
            {steps.map((s, i) => (
              <span
                key={s}
                className="h-[3px] flex-1 rounded-full transition-colors duration-300"
                style={{ background: i <= index ? accent : 'rgb(var(--line))' }}
              />
            ))}
          </div>
        )}
      </div>

      {/* -------------------------------- body ---------------------------------- */}
      <div className="flex-1 px-5 py-5 sm:px-6">
        {(title || blurb) && (
          <div className="mb-5">
            {title && <h1 className="text-lg font-semibold tracking-tight text-ink">{title}</h1>}
            {blurb && <p className="mt-1 text-sm leading-relaxed text-muted">{blurb}</p>}
          </div>
        )}

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ------------------------------- footer --------------------------------- */}
      <div
        className="border-t px-5 py-3 sm:px-6"
        style={{ borderColor: 'rgb(var(--line-soft))' }}
      >
        <p className="flex items-center justify-center gap-1.5 text-[11px] text-faint">
          <Lock className="h-3 w-3" aria-hidden="true" />
          Secured by DistrxPay
        </p>
      </div>
    </div>
  );
}

/**
 * The primary action, sized for a thumb and never ambiguous about what it does.
 * Always says the amount on the step that actually takes the money.
 */
export function StepButton({ accent, busy, disabled, children, onClick, type = 'button' }) {
  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      whileTap={disabled || busy ? {} : { scale: 0.985 }}
      className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-lg text-[15px] font-semibold text-white shadow-sm transition-[filter,opacity] hover:brightness-[0.94] disabled:opacity-50"
      style={{ background: accent }}
    >
      {children}
    </motion.button>
  );
}

export default StepFrame;
